/**
 * What a note across a share says once opened (decision 76; part 38e log, DN8, table PN): a first note, on a chapter (at
 * a passage, part 23c's anchor, or on the whole chapter) or in the Notebook (with a title and a section), or a reply
 * naming the note it answers by its ID at the relay; each with its words as plain text (decision 59). Who wrote it, its
 * group and thread are the note's place (sealed with it, DN6), not its words. UTF-8 JSON. `readShareNote` takes what an
 * invitee, a writer device or the relay wrote: anything but exactly a note item within its bounds is null, never stored.
 * Pure.
 */
import { anchorJson, anchorOf, exactly, fit, ID, MAX_ITEM_TEXT, UTF8 } from './item.ts';
import type { ItemAnchor } from './item.ts';

/** The version written. */
export const SHARE_NOTE_VERSION = 1;

/** The most UTF-16 units a Notebook note's title or section holds. */
export const MAX_SHARE_NOTE_TITLE = 200;

/** A note across a share, opened. */
export type ShareNote =
  | {
      readonly kind: 'note';
      /** Its chapter (32 lowercase hex digits), or null in the Notebook. */
      readonly chapter: string | null;
      /** Its passage on the chapter; null on the whole chapter and in the Notebook. */
      readonly anchor: ItemAnchor | null;
      /** A Notebook note's title and section; empty on a chapter. */
      readonly title: string;
      readonly section: string;
      readonly text: string;
    }
  | {
      readonly kind: 'reply';
      /** The note it answers, its ID at the relay as 32 lowercase hex digits. */
      readonly to: string;
      readonly text: string;
    };

const NOTE_KEYS = ['v', 'kind', 'chapter', 'anchor', 'title', 'section', 'text'] as const;
const REPLY_KEYS = ['v', 'kind', 'to', 'text'] as const;

/** `note` as its bytes. */
export function encodeShareNote(note: ShareNote): Uint8Array<ArrayBuffer> {
  const body =
    note.kind === 'note'
      ? { v: SHARE_NOTE_VERSION, kind: note.kind, chapter: note.chapter, anchor: note.anchor === null ? null : anchorJson(note.anchor), title: note.title, section: note.section, text: note.text }
      : { v: SHARE_NOTE_VERSION, kind: note.kind, to: note.to, text: note.text };
  return new TextEncoder().encode(JSON.stringify(body));
}

/** Words that say something: not empty once trimmed. */
function said(text: string): boolean {
  return text.trim() !== '';
}

/**
 * PN11: the note `bytes` hold, or null: not strict UTF-8 JSON, another version, a kind but a note or a reply, a field
 * more or less, an ID of the wrong form, text over its bound or holding a NUL or a lone surrogate, an anchor out of its
 * bounds or off a chapter, a title or section on a chapter, or nothing said (a reply's or a chapter note's words empty; a
 * Notebook note's words and title both).
 */
export function readShareNote(bytes: Uint8Array): ShareNote | null {
  let value: unknown;
  try {
    value = JSON.parse(UTF8.decode(bytes));
  } catch {
    return null;
  }
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  const fields = exactly(value, kind === 'note' ? NOTE_KEYS : REPLY_KEYS);
  if (fields?.get('v') !== SHARE_NOTE_VERSION) return null;
  const text = fit(fields.get('text'), MAX_ITEM_TEXT);
  if (text === null) return null;
  if (kind === 'note') {
    const given = fields.get('chapter');
    const chapter = given === null ? null : typeof given === 'string' && ID.test(given) ? given : undefined;
    const raw = fields.get('anchor');
    const anchor = raw === null ? null : anchorOf(raw);
    const title = fit(fields.get('title'), MAX_SHARE_NOTE_TITLE);
    const section = fit(fields.get('section'), MAX_SHARE_NOTE_TITLE);
    if (chapter === undefined || (raw !== null && anchor === null) || title === null || section === null) return null;
    if (chapter === null ? anchor !== null || !(said(text) || said(title)) : title !== '' || section !== '' || !said(text)) return null;
    return { kind, chapter, anchor, title, section, text };
  }
  const to = fields.get('to');
  if (kind !== 'reply' || typeof to !== 'string' || !ID.test(to) || !said(text)) return null;
  return { kind, to, text };
}
